import { useRef } from "react"
import { ImagePlus } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { errorMessage } from "@/lib/errors"

type ImageUploadControlProps = {
  label: string
  hint?: string
  imageUrl?: string | null
  fallback: React.ReactNode
  uploading?: boolean
  onUpload: (file: File) => void | Promise<void>
  onRemove?: () => void | Promise<void>
  shape?: "circle" | "square"
  accept?: string
  maxLabel?: string
}

export function ImageUploadControl({
  label,
  hint,
  imageUrl,
  fallback,
  uploading = false,
  onUpload,
  onRemove,
  shape = "square",
  accept = "image/jpeg,image/png,image/webp,image/gif",
  maxLabel = "5 MB max · JPG, PNG, WebP",
}: ImageUploadControlProps) {
  const fileRef = useRef<HTMLInputElement>(null)

  // `onRemove` is a caller-supplied mutation call; without this the rejection
  // is an unhandled promise and the user sees nothing at all.
  const remove = async () => {
    try {
      await onRemove?.()
    } catch (e) {
      toast.error(errorMessage(e, "Failed to remove"))
    }
  }

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-[color:var(--wf-ink)]">{label}</p>
      <div className="flex flex-wrap items-center gap-4">
        <div
          className={cn(
            "relative flex size-20 shrink-0 items-center justify-center overflow-hidden border border-[rgb(12_15_20/0.12)] bg-[rgb(12_15_20/0.03)]",
            shape === "circle" ? "rounded-full" : "rounded-[0.75rem]",
          )}
        >
          {imageUrl ? (
            <img src={imageUrl} alt="" className="size-full object-cover" />
          ) : (
            fallback
          )}
        </div>
        <div className="flex min-w-0 flex-col gap-2">
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={uploading}
              onClick={() => fileRef.current?.click()}
            >
              <ImagePlus className="size-4" />
              {uploading ? "Uploading…" : imageUrl ? "Replace" : "Upload"}
            </Button>
            {imageUrl && onRemove && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={uploading}
                onClick={() => void remove()}
              >
                Remove
              </Button>
            )}
          </div>
          <p className="text-xs text-[color:var(--wf-mute)]">
            {hint ?? maxLabel}
          </p>
        </div>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) void onUpload(file)
          if (fileRef.current) fileRef.current.value = ""
        }}
      />
    </div>
  )
}
